import { useState, useEffect, useRef } from "react";
import QRCode from "qrcode";
import { Copy, Check, QrCode, Link2, Users, DollarSign, TrendingUp, Award, MessageCircle, Send, Twitter, Linkedin, Mail, X } from "lucide-react";
import { toast } from "sonner";
import SocialShareButtons from "./SocialShareButtons";

import { useTranslation } from 'react-i18next';
function StatCard({ icon: Icon, label, value, subValue, color = "text-amber-400" }) {
  return (
    <div className="border border-zinc-800 bg-zinc-950/30 p-4 hover:border-zinc-700 transition-colors">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
      </div>
      <div className={`font-display text-xl sm:text-2xl ${color}`}>{value}</div>
      {subValue && <div className="font-mono text-[11px] text-zinc-600 mt-1">{subValue}</div>}
    </div>
  );
}

function LeaderboardRow({ entry, rank }) {
  const rankColors = ["text-amber-400", "text-zinc-300", "text-amber-700"];
  const rankColor = rank <= 3 ? rankColors[rank - 1] : "text-zinc-500";

  return (
    <div className={`flex items-center justify-between p-3 border-b border-zinc-800 last:border-0 ${entry.isCurrentUser ? "bg-amber-500/5 border-l-2 border-l-amber-500" : ""}`}>
      <div className="flex items-center gap-3">
        <span className={`font-mono text-sm font-bold ${rankColor} w-6 text-center`}>
          {rank <= 3 ? ["🥇", "🥈", "🥉"][rank - 1] : `#${rank}`}
        </span>
        <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center">
          <span className="font-mono text-[10px] text-zinc-400">
            {entry.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
          </span>
        </div>
        <div>
          <span className={`font-mono text-[12px] ${entry.isCurrentUser ? "text-amber-400 font-bold" : "text-zinc-300"}`}>
            {entry.name}
          </span>
          {entry.isCurrentUser && (
            <span className="font-mono text-[9px] uppercase tracking-wider text-amber-500 ml-2">{t('referral_dashboard.you')}</span>
          )}
        </div>
      </div>
      <div className="text-right">
        <div className="font-mono text-[12px] text-white">{entry.referralCount} refs</div>
        <div className="font-mono text-[10px] text-zinc-500">${entry.credits}</div>
      </div>
    </div>
  );
}

export default function ReferralDashboard({ referralLink, referralCode, stats, tierInfo, leaderboard, onCopyLink, onShareWhatsApp, onShareTelegram, onShareTwitter, onShareLinkedIn, onShareEmail }) {
  const { t } = useTranslation();
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [showMobileShare, setShowMobileShare] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (referralLink) {
      QRCode.toDataURL(referralLink, {
        width: 200,
        margin: 2,
        color: { dark: "#f59e0b", light: "#09090b" },
      }).then(setQrDataUrl).catch(() => {});
    }
  }, [referralLink]);

  const handleCopy = () => {
    onCopyLink();
    setCopied(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-8">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} label={t('referral_dashboard.total_referrals')} value={stats.totalReferrals} subValue={`${stats.activeReferrals} active`} color="text-amber-400" />
        <StatCard icon={TrendingUp} label={t('referral_dashboard.conversion_rate')} value={`${stats.conversionRate}%`} subValue={`${stats.convertedCount} converted`} color="text-emerald-400" />
        <StatCard icon={DollarSign} label={t('referral_dashboard.credits_earned')} value={`$${stats.credits}`} subValue={`$${stats.baseCredits} + $${stats.milestoneBonuses} bonus`} color="text-amber-400" />
        <StatCard icon={Award} label={t('referral_dashboard.current_tier')} value={tierInfo.current} subValue={tierInfo.next ? `${tierInfo.referralsToNext} more for ${tierInfo.next}` : "Max tier!"} color="text-violet-400" />
      </div>

      {/* Referral Link + QR Code */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Link Card */}
        <div className="border border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-transparent p-6">
          <div className="flex items-center gap-2 mb-4">
            <Link2 className="w-4 h-4 text-amber-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400">{t('referral_dashboard.your_referral_link')}</span>
          </div>

          <div className="mb-3">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-1">{t('referral_dashboard.referral_code')}</label>
            <div className="flex items-center gap-2 p-2 bg-zinc-950/60 border border-zinc-800">
              <code className="font-mono text-sm text-amber-400 font-bold tracking-widest">{referralCode}</code>
            </div>
          </div>

          <div className="mb-4">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-1">{t('referral_dashboard.full_link')}</label>
            <div className="flex items-center gap-2 p-3 bg-zinc-950/60 border border-zinc-800">
              <code className="font-mono text-[12px] text-amber-400 truncate flex-1">{referralLink}</code>
              <button
                onClick={handleCopy}
                className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-black font-mono text-[10px] uppercase tracking-wider hover:bg-amber-400 transition-colors"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>

          <SocialShareButtons
            referralLink={referralLink}
            onShareWhatsApp={onShareWhatsApp}
            onShareTelegram={onShareTelegram}
            onShareTwitter={onShareTwitter}
            onShareLinkedIn={onShareLinkedIn}
            onShareEmail={onShareEmail}
            onCopyLink={onCopyLink}
          />

          {/* Mobile Share Button */}
          <button
            onClick={() => setShowMobileShare(true)}
            className="md:hidden mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 font-mono text-[11px] text-amber-400 uppercase tracking-wider transition-colors"
          >
            Share via...
          </button>
        </div>

        {/* QR Code Card */}
        <div className="border border-zinc-800 bg-zinc-950/30 p-6 flex flex-col items-center justify-center">
          <div className="flex items-center gap-2 mb-4 self-start">
            <QrCode className="w-4 h-4 text-amber-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400">{t('referral_dashboard.qr_code')}</span>
          </div>
          {qrDataUrl ? (
            <div className="bg-zinc-950 p-3 sm:p-4 border border-zinc-800">
              <img src={qrDataUrl} alt={t('referral_dashboard.referral_qr_code')} className="w-28 h-28 sm:w-40 sm:h-40" loading="lazy" decoding="async" />
            </div>
          ) : (
            <div className="w-28 h-28 sm:w-40 sm:h-40 bg-zinc-900 animate-pulse border border-zinc-800" />
          )}
          <p className="font-mono text-[10px] text-zinc-500 mt-3 text-center">{t('referral_dashboard.scan_to_share_your_referral_link')}</p>
        </div>
      </div>

      {/* Tier Progress */}
      {tierInfo.next && (
        <div className="border border-zinc-800 bg-zinc-950/30 p-6">
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Progress to {tierInfo.next} Tier
            </span>
            <span className="font-mono text-[11px] text-amber-400">
              {stats.convertedCount}/{tierInfo.nextMilestone} referrals
            </span>
          </div>
          <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-500"
              style={{ width: `${Math.min(tierInfo.progress, 100)}%` }}
            />
          </div>
          <p className="font-mono text-[10px] text-zinc-500 mt-2">
            {tierInfo.referralsToNext} more referral{tierInfo.referralsToNext !== 1 ? "s" : ""} to unlock {tierInfo.nextRate}/referral + ${tierInfo.milestoneBonus} bonus
          </p>
        </div>
      )}

      {/* Leaderboard */}
      <div className="border border-zinc-800 bg-zinc-950/30">
        <div className="p-4 border-b border-zinc-800">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400">{t('referral_dashboard.top_referrers')}</span>
        </div>
        <div className="divide-y divide-zinc-800">
          {leaderboard.map((entry, i) => (
            <LeaderboardRow key={entry.id} entry={entry} rank={i + 1} />
          ))}
        </div>
      </div>

      {/* Mobile Share Bottom Sheet */}
      {showMobileShare && (
        <div className="md:hidden fixed inset-0 z-50 flex items-end justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowMobileShare(false)}
          />
          {/* Sheet */}
          <div className="relative w-full max-w-lg bg-zinc-950 border-t border-zinc-800 p-6 pb-8 rounded-t-2xl">
            <div className="flex items-center justify-between mb-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400">{t('referral_dashboard.share_via')}</span>
              <button
                onClick={() => setShowMobileShare(false)}
                className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <button
                onClick={() => { onShareWhatsApp(); setShowMobileShare(false); }}
                className="flex flex-col items-center gap-2 p-3 border border-zinc-700 hover:border-green-500/50 hover:bg-green-500/5 transition-colors"
              >
                <MessageCircle className="w-6 h-6 text-green-400" />
                <span className="font-mono text-[10px] text-zinc-400">{t('referral.whatsapp')}</span>
              </button>
              <button
                onClick={() => { onShareTelegram(); setShowMobileShare(false); }}
                className="flex flex-col items-center gap-2 p-3 border border-zinc-700 hover:border-blue-500/50 hover:bg-blue-500/5 transition-colors"
              >
                <Send className="w-6 h-6 text-blue-400" />
                <span className="font-mono text-[10px] text-zinc-400">{t('referral.telegram')}</span>
              </button>
              <button
                onClick={() => { onShareTwitter(); setShowMobileShare(false); }}
                className="flex flex-col items-center gap-2 p-3 border border-zinc-700 hover:border-sky-500/50 hover:bg-sky-500/5 transition-colors"
              >
                <Twitter className="w-6 h-6 text-sky-400" />
                <span className="font-mono text-[10px] text-zinc-400">{t('referral.twitter')}</span>
              </button>
              <button
                onClick={() => { onShareLinkedIn(); setShowMobileShare(false); }}
                className="flex flex-col items-center gap-2 p-3 border border-zinc-700 hover:border-blue-600/50 hover:bg-blue-600/5 transition-colors"
              >
                <Linkedin className="w-6 h-6 text-blue-500" />
                <span className="font-mono text-[10px] text-zinc-400">{t('referral.linkedin')}</span>
              </button>
              <button
                onClick={() => { onShareEmail(); setShowMobileShare(false); }}
                className="flex flex-col items-center gap-2 p-3 border border-zinc-700 hover:border-amber-500/50 hover:bg-amber-500/5 transition-colors"
              >
                <Mail className="w-6 h-6 text-amber-400" />
                <span className="font-mono text-[10px] text-zinc-400">{t('referral_dashboard.email')}</span>
              </button>
              <button
                onClick={() => { onCopyLink(); setShowMobileShare(false); toast.success("Link copied!"); }}
                className="flex flex-col items-center gap-2 p-3 border border-zinc-700 hover:border-amber-500/50 hover:bg-amber-500/5 transition-colors"
              >
                <Copy className="w-6 h-6 text-amber-400" />
                <span className="font-mono text-[10px] text-zinc-400">{t('referral_dashboard.copy_link')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
